export interface ParsedEntry {
  name: string;
  quantity: number;
  isCommander: boolean;
}

type Section = 'deck' | 'commander' | 'sideboard';

/**
 * Parses a pasted deck list into structured entries.
 *
 * Supported formats:
 *  - "1 Card Name"  /  "1x Card Name"         (MTGO, Moxfield, Archidekt)
 *  - "Card Name x1" / "Card Name x 1"
 *  - "Card Name"                               (implicit qty 1)
 *  - "1 Card Name (SET) 123"                   (Archidekt with set code — stripped)
 *  - "*CMDR*" suffix marks commander
 *  - Section headers: "// Commander", "Commander", "// Deck", "Deck", etc.
 *  - Moxfield plain text: commander(s) after the last blank line (1–2 cards)
 */
export function parseDeckList(text: string): ParsedEntry[] {
  const entries: ParsedEntry[] = [];
  let section: Section = 'deck';
  let hasExplicitCommander = false;

  // Track blank-line groups for Moxfield detection
  // Each group is the entries parsed from a contiguous block of lines
  const groups: ParsedEntry[][] = [[]];

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();

    if (!line) {
      // Blank line: start a new group (if current group has entries)
      if (groups[groups.length - 1]!.length > 0) {
        groups.push([]);
      }
      continue;
    }

    // Section headers
    const sectionLower = line.toLowerCase().replace(/^\/\/\s*/, '').trim();
    if (/^(commander|commanders)$/.test(sectionLower)) { section = 'commander'; hasExplicitCommander = true; continue; }
    if (/^(deck|mainboard|main|library)$/.test(sectionLower)) { section = 'deck'; continue; }
    if (/^(sideboard|side)$/.test(sectionLower)) { section = 'sideboard'; continue; }

    // Skip comment-only lines and sideboard
    if (line.startsWith('//') || line.startsWith('#')) continue;
    if (section === 'sideboard') continue;

    // Detect *CMDR* flag
    const hasCmdrFlag = /\*CMDR\*/i.test(line);
    if (hasCmdrFlag) hasExplicitCommander = true;
    let workingLine = line.replace(/\*CMDR\*/i, '').trim();

    // Strip trailing set code: " (SET) 123" or " [SET] 123"
    workingLine = workingLine.replace(/\s+[\[(][A-Z0-9]{2,5}[\])]\s*\d*\s*$/, '').trim();

    // Strip trailing tags like "#commander", "(Commander)"
    workingLine = workingLine.replace(/\s+(#commander|\(commander\))$/i, '').trim();

    let quantity = 1;
    let name = workingLine;

    // "1 Card Name" or "1x Card Name"
    const prefixMatch = workingLine.match(/^(\d+)[xX]?\s+(.+)$/);
    if (prefixMatch) {
      quantity = parseInt(prefixMatch[1]!, 10);
      name = prefixMatch[2]!.trim();
    } else {
      // "Card Name x1"
      const suffixMatch = workingLine.match(/^(.+?)\s+x\s*(\d+)$/i);
      if (suffixMatch) {
        name = suffixMatch[1]!.trim();
        quantity = parseInt(suffixMatch[2]!, 10);
      }
    }

    if (!name || quantity < 1) continue;

    const entry: ParsedEntry = {
      name,
      quantity,
      isCommander: hasCmdrFlag || section === 'commander',
    };

    entries.push(entry);
    groups[groups.length - 1]!.push(entry);
  }

  // Moxfield heuristic: if no explicit commander was found, and the last group
  // has 1–2 cards separated by a blank line, treat them as commanders
  if (!hasExplicitCommander && groups.length >= 2) {
    const lastGroup = groups[groups.length - 1]!;
    const totalCards = lastGroup.reduce((sum, e) => sum + e.quantity, 0);
    if (totalCards >= 1 && totalCards <= 2) {
      for (const entry of lastGroup) {
        entry.isCommander = true;
      }
    }
  }

  return entries;
}
